<template>
  <LoadingComponent v-if="loading" />
  <template v-if="!loading && localDashboard">
    <div :key="localDashboard.id" class="flex-shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            ref="panel_control"
            v-model:search-value="searchValue"
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            :need-save-locked="loading"
            @dashboard-changed="dashboardChanged"
            @clear-filters="clearFilters"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template v-if="false" #display-options-content>
              <DisplayOptionsContent
                v-model:display-options="localDashboard.display_options"
              />
            </template>
            <template #other-cta>
              <AddNewColumnComponent
                v-if="$can('manage_dashboards') && !isLockedForMe"
                @add-column="addColumn"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4">
      <AlertPanel
        v-if="isListTruncated"
        type="warning"
        compact
        :title="truncationMessage"
      />
      <DashBoardTable
        v-model:columns="localDashboard.columns"
        v-model:sort="localDashboard.sort"
        v-model:require-save-dashboard="requireSaveDashboard"
        :dossiers="filtered_dossier"
        :loading="loadingDossier"
        @fetch-dossier="fetchDossier"
        @on:tasks-visible="(v) => (tableContentOverflowAuto = v)"
        @update:dashboard="updateDashboard"
      />
    </div>
  </template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import AlertPanel from '@ui/AlertPanel.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import DashBoardTable from '@c/Dashboard/Content/Dossier/DashBoardTable.vue'
import DashboardPanelControl from '../../Components/DashboardPanelControl.vue'
import DisplayOptionsContent from '@c/Dashboard/Content/Dossier/Panel/DisplayOptionsContent.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import AddNewColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/columnSettings/AddNewColumnComponent.vue'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'
import { isDashboardLockedFor } from '@u/helpers/dashboardPermissions'

export default {
  name: 'DossierContent',
  components: {
    AlertPanel,
    AddNewColumnComponent,
    DisplayOptionsContent,
    FilterLinesComponent,
    DashboardPanelControl,
    DashBoardTable,
    DashboardHeader,
    LoadingComponent
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: ['searchValue', 'localDashboard.filters'],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingDossier: false,
      searchValue: '',
      dossierList: [],
      localDashboard: null,
      tableContentOverflowAuto: true,
      requireSaveDashboard: false,
      loadMore: true,
      paginator: null
    }
  },
  computed: {
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      availableFilter: 'metadata/getAvailableFilterDossier',
      auth: 'auth/getAuth'
    }),
    isLockedForMe() {
      // A locked dashboard is read-only for everyone but its creator, except
      // for the roles that administer every dashboard (BBSW-10846).
      return isDashboardLockedFor(this.auth, this.localDashboard)
    },
    filtered_dossier() {
      return this.dossierList
        .when(this.searchValue.length >= 2, (dossierList) =>
          dossierList.filter((dossier) => {
            return this.$h
              .normalizeSearch(dossier.searchable_field)
              .includes(this.$h.normalizeSearch(this.searchValue))
          })
        )
        .when(this.localDashboard.sort?.column_id, (dossierList) => {
          let sortRealColumn = this.localDashboard.columns.find(
            (column) => column.id === this.localDashboard.sort.column_id
          )
          let sortRealColumnValue =
            sortRealColumn?.sort?.value ?? sortRealColumn?.field

          return dossierList
            .when(sortRealColumnValue, (dossierList) => {
              return dossierList.sort((a, b) => {
                let aRaw = a[sortRealColumnValue] ?? ''
                let bRaw = b[sortRealColumnValue] ?? ''
                let aValue =
                  aRaw !== null && typeof aRaw === 'object'
                    ? String(aRaw.status ?? '')
                    : String(aRaw)
                let bValue =
                  bRaw !== null && typeof bRaw === 'object'
                    ? String(bRaw.status ?? '')
                    : String(bRaw)
                return aValue.localeCompare(bValue)
              })
            })
            .when(
              this.localDashboard.sort.direction === 'desc',
              (dossierList) => dossierList.reverse()
            )
        })
    },
    // The API caps the page it returns; the rows beyond that cap are simply absent, and
    // nothing else on screen says so (BBSW-11198).
    isListTruncated() {
      return !!this.paginator && this.paginator.total > this.paginator.per_page
    },
    truncationMessage() {
      if (!this.isListTruncated) {
        return ''
      }

      return this.$t('global.dossiers_list_truncated', {
        shown: this.paginator.per_page,
        total: this.paginator.total
      })
    },
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchTemplateDashboardConfig()
      }
    },
    '$i18n.locale': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchDossier()
      }
    }
  },
  mounted() {
    this.fetchTemplateDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchDossierListAction: 'dashboard/fetchListDossier',
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.fetchDossier()
    },
    fetchTemplateDashboardConfig() {
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState(
            'localDashboard.filters',
            response.template_dashboard.filters
          )
          this.fetchDossier()
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
    },
    fetchDossier() {
      if (this.localDashboard.columns) {
        this.loadingDossier = true
        this.paginator = null
        this.processFetchDossierListAction({
          filters: this.localDashboard.filters,
          columns: this.localDashboard.columns,
          template_id: this.localDashboard.id
        })
          .then((response) => {
            this.dossierList = response.data.dossiers
            this.paginator = response.data.paginator ?? null
          })
          .catch(() => {})
          .finally((_) => {
            this.loadingDossier = false
          })
      }
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchDossier()
    },
    dashboardChanged(dashboard_template_id) {},
    addColumn(config) {
      this.localDashboard.columns.push({
        ...config,
        template_id: this.localDashboard.id
      })
    },
    configUpdated() {},
    updateDashboard(reloadDossier) {
      this.$refs.panel_control.saveDashboard(() => {
        if (reloadDossier) {
          this.fetchDossier()
        }
      })
    }
  }
}
</script>

<style scoped></style>
